/**
 * URL Filtering Hook
 * 
 * Custom hook that provides URL-based filtering functionality.
 * Manages filter state, URL synchronization, and provides helper functions.
 * 
 * @module useURLFiltering
 */

import { useState, useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { DEFAULT_FILTERS } from '../constants/filter-constants';

/**
 * Custom hook for URL-based filtering
 * 
 * @param {string} module - Module name ('facility', 'user', 'provider')
 * @returns {Object} Filtering utilities and state
 * 
 * @example
 * const {
 *   selectedFilters,
 *   updateFilters,
 *   getAPIParams,
 *   resetFilters
 * } = useURLFiltering('facility');
 */
export const useURLFiltering = (module) => {
  const [searchParams, setSearchParams] = useSearchParams();
  
  /**
   * Gets filters from URL parameters or returns defaults
   * 
   * @returns {Object} Filter object from URL
   */
  const getFiltersFromURL = useCallback(() => {
    const defaultFilters = DEFAULT_FILTERS[module] || { status: 'active' };
    const urlFilters = {};
    
    // Extract all filter parameters from URL
    Object.keys(defaultFilters).forEach(key => {
      const urlValue = searchParams.get(key);
      urlFilters[key] = urlValue || defaultFilters[key];
    });
    
    // Handle additional potential filters
    const additionalFilters = ['role', 'specialty', 'facility', 'location', 'plans', 'branch', 'usecase', 'subUsecase', 'uploaderName', 'search'];
    additionalFilters.forEach(key => {
      const urlValue = searchParams.get(key);
      if (urlValue) {
        urlFilters[key] = urlValue;
      }
    });
    
    return urlFilters;
  }, [searchParams, module]);

  const [selectedFilters, setSelectedFilters] = useState(getFiltersFromURL());

  /**
   * Updates URL with filter parameters
   * 
   * @param {Object} filters - Filter object to sync to URL
   */
  const updateURLWithFilters = useCallback((filters) => {
    const params = new URLSearchParams();
    const defaultFilters = DEFAULT_FILTERS[module] || { status: 'active' };
    
    Object.entries(filters).forEach(([key, value]) => {
      // Only add non-default values to keep URLs clean
      if (value && value !== defaultFilters[key]) {
        params.set(key, value);
      }
    });
    
    setSearchParams(params);
  }, [setSearchParams, module]);

  /**
   * Converts filters to API parameters
   * 
   * @param {Object} filters - Filter object
   * @returns {Object} API parameters
   */
  const getAPIParams = useCallback((filters) => {
    const params = {};
    
    // Handle status filter (common across all modules except batch)
    // Batch module has its own status handling
    if (module !== 'batch') {
      // Default behavior (active): don't include the parameter
      // Inactive/All: include_inactive = true
      if (filters.status === 'inactive' || filters.status === 'all') {
        params.include_inactive = true;
      }
      // For 'active' status, we don't set include_inactive parameter at all
    }
    
    // Module-specific parameter handling
    switch (module) {
      case 'user':
        if (filters.role) params.role = filters.role;
        if (filters.facility) params.facility_id = filters.facility;
        break;
        
      case 'provider':
        if (filters.specialty) params.specialty = filters.specialty;
        if (filters.facility) params.facility_id = filters.facility;
        break;
        
      case 'facility':
        if (filters.location) params.location = filters.location;
        if (filters.plans) params.plan_type = filters.plans;
        break;
        
      case 'department':
        if (filters.facility) params.facilityId = filters.facility;
        break;
        
        case 'batch':
          if (filters.status && filters.status !== 'all') params.status = filters.status;
          if (filters.branch && filters.branch !== 'all') params.branch = filters.branch;
          if (filters.usecase && filters.usecase !== 'all') params.usecase = filters.usecase;
          if (filters.subUsecase && filters.subUsecase !== 'all') params.subUsecase = filters.subUsecase;
          if (filters.uploaderName && filters.uploaderName !== 'all') params.uploaderName = filters.uploaderName;
          if (filters.search && filters.search.trim()) params.search = filters.search.trim();
          break;
        
      default:
        // Handle any additional filters generically
        Object.entries(filters).forEach(([key, value]) => {
          if (key !== 'status' && value) {
            params[key] = value;
          }
        });
    }
    
    return params;
  }, [module]);

  /**
   * Updates filters and syncs to URL
   * 
   * @param {Object} newFilters - New filter values
   */
  const updateFilters = useCallback((newFilters) => {
    console.log(`${module} filters changed to:`, newFilters);
    
    // Update URL first
    updateURLWithFilters(newFilters);
    
    // Update state
    setSelectedFilters(newFilters);
  }, [module, updateURLWithFilters]);

  /**
   * Resets filters to default values
   */
  const resetFilters = useCallback(() => {
    const defaultFilters = DEFAULT_FILTERS[module] || { status: 'active' };
    updateFilters(defaultFilters);
  }, [module, updateFilters]);

  /**
   * Checks if filters are at default values
   * 
   * @returns {boolean} True if filters are at defaults
   */
  const isDefaultFilters = useCallback(() => {
    const defaultFilters = DEFAULT_FILTERS[module] || { status: 'active' };
    return JSON.stringify(selectedFilters) === JSON.stringify(defaultFilters);
  }, [selectedFilters, module]);

  // Handle URL changes (browser back/forward, direct URL access, page refresh)
  useEffect(() => {
    const filtersFromURL = getFiltersFromURL();
    console.log(`${module} URL changed, filters from URL:`, filtersFromURL);
    
    // Check if URL filters are different from current state
    if (JSON.stringify(filtersFromURL) !== JSON.stringify(selectedFilters)) {
      console.log(`Updating ${module} filters from URL change`);
      setSelectedFilters(filtersFromURL);
    }
  }, [getFiltersFromURL, selectedFilters, module]);

  return {
    selectedFilters,
    updateFilters,
    getAPIParams,
    resetFilters,
    isDefaultFilters,
    getFiltersFromURL
  };
};
